import { useState, useRef, useEffect, useCallback } from 'react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import { formatLocation } from '../../utils/formatLocation';

export const TeamDropdown = ({
  selectedTeam,
  onSelectTeam,
  teams,
  loading = false,
  placeholder = "SELECT TEAM",
  loadingText = "Loading teams...",
  className = "",
  variant = "default", // "default", "form", "themed", or "admin"
  searchPlaceholder = "Search teams...",
  // New props for API search and infinite scroll
  onSearch,           // Callback for API search (called with debounce)
  onLoadMore,         // Callback for loading more teams
  hasMore = false,    // Whether there are more teams to load
  loadingMore = false // Loading state for infinite scroll
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Handle search with debounce for API search
  const handleSearchChange = (value) => {
    setSearchTerm(value);

    // If onSearch is provided, debounce and call it for API search
    if (onSearch) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        onSearch(value);
      }, 500);
    }
  };

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    if (!listRef.current || loading || loadingMore || !hasMore || !onLoadMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    // Load more when user is near bottom (30px threshold)
    if (scrollHeight - scrollTop - clientHeight < 30) {
      onLoadMore();
    }
  }, [loading, loadingMore, hasMore, onLoadMore]);

  // Filter teams based on search term (local filter if no API search)
  const filteredTeams = onSearch
    ? teams // If API search is enabled, show all teams (API handles filtering)
    : teams.filter((team) => {
        const teamName = team.name || team.teamName || '';
        return teamName.toLowerCase().includes(searchTerm.toLowerCase());
      });

  const handleSelectTeam = (team) => {
    onSelectTeam(team);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Get display name for selected team
  const getTeamDisplayName = () => {
    if (!selectedTeam) return placeholder;
    if (typeof selectedTeam === 'string') {
      const team = teams.find(t => (t.id || t._id) === selectedTeam);
      return team?.name || team?.teamName || placeholder;
    }
    return selectedTeam.name || selectedTeam.teamName || placeholder;
  };

  // Admin variant (for admin forms)
  if (variant === 'admin') {
    return (
      <div className={`relative h-full ${className}`} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !loading && setIsOpen(!isOpen)}
          disabled={loading}
          className="w-full h-full px-3 rounded-lg bg-jp-card border border-jp-line text-xs text-jp-ink text-left flex items-center justify-between focus:outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className={selectedTeam ? 'text-jp-ink' : 'text-jp-muted'}>
            {loading ? loadingText : getTeamDisplayName()}
          </span>
          <FiChevronDown
            className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            size={14}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && !loading && (
          <div className="absolute top-full right-0 mt-2 min-w-[250px] bg-jp-section border border-jp-line rounded-lg shadow-xl z-50 overflow-hidden">
            {/* Search Bar */}
            <div className="p-2 border-b border-jp-line">
              <div className="relative">
                <FiSearch
                  className="absolute left-2 top-1/2 -translate-y-1/2 text-jp-muted"
                  size={14}
                />
                <input
                  type="text"
                  placeholder="Search teams..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-8 pr-2 py-1.5 bg-jp-card border border-jp-line rounded text-jp-ink text-xs placeholder-jp-muted/70 focus:outline-none focus:border-sky-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Team List */}
            <div
              ref={listRef}
              onScroll={handleScroll}
              className="max-h-48 overflow-y-auto"
            >
              {/* All Teams option */}
              <button
                type="button"
                onClick={() => handleSelectTeam(null)}
                className={`w-full text-left px-3 py-2 text-xs transition-colors border-b border-jp-line ${
                  !selectedTeam
                    ? 'bg-sky-600/20 text-sky-600 dark:text-sky-300 font-semibold'
                    : 'text-jp-ink hover:bg-jp-line'
                }`}
              >
                All Teams
              </button>

              {filteredTeams.length > 0 ? (
                <>
                  {filteredTeams.map((team) => {
                    const teamId = team.id || team._id;
                    const teamName = team.name || team.teamName;
                    const isSelected = selectedTeam === teamId ||
                                     selectedTeam?.id === teamId ||
                                     selectedTeam?._id === teamId;

                    return (
                      <button
                        key={teamId}
                        type="button"
                        onClick={() => handleSelectTeam(team)}
                        className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                          isSelected
                            ? 'bg-sky-600/20 text-sky-600 dark:text-sky-300 font-semibold'
                            : 'text-jp-ink hover:bg-jp-line'
                        }`}
                      >
                        <div className="font-medium">{teamName}</div>
                        {team.location && (
                          <div className="text-[10px] text-jp-muted mt-0.5">
                            {formatLocation(team.location)}
                          </div>
                        )}
                      </button>
                    );
                  })}
                  {/* Loading more indicator */}
                  {loadingMore && (
                    <div className="px-3 py-2 text-center">
                      <div className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </div>
                  )}
                  {/* Load more trigger area */}
                  {hasMore && !loadingMore && (
                    <div className="px-3 py-1 text-center text-jp-muted text-[10px]">
                      Scroll for more...
                    </div>
                  )}
                </>
              ) : loading ? (
                <div className="px-3 py-6 text-center">
                  <div className="w-5 h-5 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              ) : (
                <div className="px-3 py-6 text-center text-jp-muted text-xs">
                  No teams found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Themed variant (form field on light/dark pages, matches the jp-* inputs)
  if (variant === 'themed') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !loading && setIsOpen(!isOpen)}
          disabled={loading}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className="w-full px-4 py-3 rounded-xl bg-jp-field border border-jp-line text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
        >
          <span className={selectedTeam ? 'text-jp-ink' : 'text-jp-muted/70'}>
            {loading ? loadingText : getTeamDisplayName()}
          </span>
          <FiChevronDown
            className={`text-jp-muted transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            size={18}
          />
        </button>

        {isOpen && !loading && (
          <div className="absolute top-full left-0 mt-2 w-full bg-jp-card border border-jp-line rounded-xl shadow-xl z-50 overflow-hidden">
            {/* Search Bar */}
            <div className="p-3 border-b border-jp-line">
              <div className="relative">
                <FiSearch
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-jp-muted"
                  size={16}
                />
                <input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 bg-jp-field border border-jp-line rounded-lg text-jp-ink text-sm placeholder-jp-muted/70 focus:outline-none focus:border-jp-accent focus:ring-1 focus:ring-jp-accent"
                  autoFocus
                />
              </div>
            </div>

            {/* Team List */}
            <div
              ref={listRef}
              onScroll={handleScroll}
              className="max-h-64 overflow-y-auto"
              role="listbox"
            >
              {filteredTeams.length > 0 ? (
                <>
                  {filteredTeams.map((team, index) => {
                    const teamId = team.id || team._id || index;
                    const teamName = team.name || team.teamName;
                    const isSelected = selectedTeam === teamId ||
                                     selectedTeam?.id === teamId ||
                                     selectedTeam?._id === teamId;

                    return (
                      <button
                        key={teamId}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => handleSelectTeam(team)}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                          isSelected
                            ? 'bg-jp-accent/10 text-jp-accent font-semibold'
                            : 'text-jp-ink hover:bg-jp-section'
                        }`}
                      >
                        <div className="font-medium">{teamName}</div>
                        {team.location && (
                          <div className="text-xs text-jp-muted mt-0.5">
                            {formatLocation(team.location)}
                          </div>
                        )}
                      </button>
                    );
                  })}
                  {/* Loading more indicator */}
                  {loadingMore && (
                    <div className="px-4 py-3 text-center">
                      <div className="w-5 h-5 border-2 border-jp-accent border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </div>
                  )}
                </>
              ) : loading ? (
                <div className="px-4 py-8 text-center">
                  <div className="w-6 h-6 border-2 border-jp-accent border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              ) : (
                <div className="px-4 py-8 text-center text-jp-muted text-sm">
                  Nothing found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Form variant (for registration pages)
  if (variant === 'form') {
    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => !loading && setIsOpen(!isOpen)}
          disabled={loading}
          className="w-full px-5 py-4 rounded-xl bg-slate-800/50 backdrop-blur-md border border-slate-600/40 text-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-cyan-400/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <span className={selectedTeam ? 'text-white' : 'text-gray-400'}>
            {loading ? loadingText : getTeamDisplayName()}
          </span>
          <FiChevronDown
            className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
            size={20}
          />
        </button>

        {/* Dropdown Menu */}
        {isOpen && !loading && (
          <div className="absolute top-full left-0 mt-2 w-full bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden">
            {/* Search Bar */}
            <div className="p-3 border-b border-slate-700">
              <div className="relative">
                <FiSearch
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={16}
                />
                <input
                  type="text"
                  placeholder="Search teams..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-md text-white text-sm placeholder-slate-400 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                  autoFocus
                />
              </div>
            </div>

            {/* Team List */}
            <div
              ref={listRef}
              onScroll={handleScroll}
              className="max-h-64 overflow-y-auto"
            >
              {filteredTeams.length > 0 ? (
                <>
                  {filteredTeams.map((team, index) => {
                    const teamId = team.id || team._id || index;
                    const teamName = team.name || team.teamName;
                    const isSelected = selectedTeam === teamId ||
                                     selectedTeam?.id === teamId ||
                                     selectedTeam?._id === teamId;

                    return (
                      <button
                        key={teamId}
                        type="button"
                        onClick={() => handleSelectTeam(team)}
                        className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                          isSelected
                            ? 'bg-cyan-600/20 text-cyan-300 font-semibold'
                            : 'text-slate-200 hover:bg-slate-700'
                        }`}
                      >
                        <div className="font-medium">{teamName}</div>
                        {team.location && (
                          <div className="text-xs text-slate-400 mt-0.5">
                            {formatLocation(team.location)}
                          </div>
                        )}
                      </button>
                    );
                  })}
                  {/* Loading more indicator */}
                  {loadingMore && (
                    <div className="px-4 py-3 text-center">
                      <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </div>
                  )}
                </>
              ) : loading ? (
                <div className="px-4 py-8 text-center">
                  <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              ) : (
                <div className="px-4 py-8 text-center text-slate-400 text-sm">
                  No teams found
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default variant (for compact displays)
  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected Team Display */}
      <button
        type="button"
        onClick={() => !loading && setIsOpen(!isOpen)}
        disabled={loading}
        className="flex items-center gap-2 text-xs font-semibold text-white tracking-wide hover:text-cyan-300 transition-colors group disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span>{loading ? loadingText : getTeamDisplayName()}</span>
        <FiChevronDown
          className={`transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          } group-hover:text-cyan-300`}
          size={14}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && !loading && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-slate-800 border border-slate-700 rounded-lg shadow-xl z-50 overflow-hidden">
          {/* Search Bar */}
          <div className="p-3 border-b border-slate-700">
            <div className="relative">
              <FiSearch
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Search teams..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-slate-900 border border-slate-600 rounded-md text-white text-sm placeholder-slate-400 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500"
                autoFocus
              />
            </div>
          </div>

          {/* Team List */}
          <div
            ref={listRef}
            onScroll={handleScroll}
            className="max-h-64 overflow-y-auto"
          >
            {filteredTeams.length > 0 ? (
              <>
                {filteredTeams.map((team, index) => {
                  const teamId = team.id || team._id || index;
                  const teamName = team.name || team.teamName;
                  const isSelected = selectedTeam === teamId ||
                                   selectedTeam?.id === teamId ||
                                   selectedTeam?._id === teamId;

                  return (
                    <button
                      key={teamId}
                      type="button"
                      onClick={() => handleSelectTeam(team)}
                      className={`w-full text-left px-4 py-3 text-sm transition-colors ${
                        isSelected
                          ? 'bg-cyan-600/20 text-cyan-300 font-semibold'
                          : 'text-slate-200 hover:bg-slate-700'
                      }`}
                    >
                      <div className="font-medium">{teamName}</div>
                      {team.location && (
                        <div className="text-xs text-slate-400 mt-0.5">
                          {formatLocation(team.location)}
                        </div>
                      )}
                    </button>
                  );
                })}
                {/* Loading more indicator */}
                {loadingMore && (
                  <div className="px-4 py-3 text-center">
                    <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </div>
                )}
              </>
            ) : loading ? (
              <div className="px-4 py-8 text-center">
                <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : (
              <div className="px-4 py-8 text-center text-slate-400 text-sm">
                No teams found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDropdown;
